import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { createEditor, Transforms } from "slate";
import { Layout } from 'antd';
import { Slate, Editable, withReact, ReactEditor } from "slate-react";
import { withHistory } from "slate-history";
import { Toolbar } from "./toolbar/toolbar";
import { Element } from "./elements/element";
import { Leaf } from "./elements/leaf";
import { toggleMark } from "./utils/slateUtils";
import withLayout from "./utils/withlayout";
import withTables from "./utils/withtable";
import { uploadImageAndInsert } from "./utils/uploadImageAndInsert";
import { uploadVideoFile } from "./utils/uploadVideoFile";
import HoveringToolbar from "./toolbar/buttons/floatingToolbar";
import withImages from "./utils/withImages";
import KoLoader from "packages/loader";
import deserializeHTMLToSlate from "./utils/deserializeHTMLToSlate";

import styles from "./fileeditor.module.scss";
import FileTypeIcon from "./fileTypeIcon";
import KoInput from "packages/input";
import KoIcon from '@kwapio/icons';
import { KoText } from "packages/typography";
// import KoDateFormatter from "packages/dateformat";
import { useSelector } from "react-redux";
import KoDP from "packages/dp";
import { getCookie } from "shared/utils/cookies";
import { useDispatch } from "react-redux";
import { getUserById } from "shared/store/userByIdSlice";
import { getSettings } from "shared/store/settingsSlice";
import KoButton from "packages/button";
import KoTooltips from "packages/tooltips";
import KoDateFormatter from "packages/dateformat";
import KoBadge from "packages/badge";
import { fixSlateDocument } from "shared/utils";

const PAGE_HEIGHT = 1123; // Approximate A4 height in px at 96 DPI
const { Header } = Layout;

const KoFileRichTextEditor = ({ initialValues, fileDetails,
  isLoading, savingTextStatus, onChangeEditorValues,
  onUpdateName, onUpdateAttachment, attachments,
  lookupId, workspaceId, commentId,
  totalComments,
  folderId, onDownload, onShare, onComments, canEdit, canComment }) => {
  const editor = useMemo(() => withLayout(withTables(withHistory(withImages(withReact(createEditor()))))), []);
  const [loading, setLoading] = useState(false);
  const [fontSize, setFontSize] = useState("14");
  const editorContainerRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [fileName, setRenameDocument] = useState(fileDetails?.name || 'Untitled Document');
  const userById = useSelector(state => state?.userById);
  const accountById = useSelector(state => state?.accountById);
  const accountDetails = accountById['data'];
  const dispatch = useDispatch();

  let userDetails = userById['data'];
  const renderElement = useCallback((props) => <Element readOnly={!canEdit} attachments={attachments} {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf readOnly={!canEdit} {...props} />, []);


  useEffect(() => {
    if (getCookie('userId')) {
      dispatch(getSettings(getCookie('accountId')));
      dispatch(getUserById(getCookie('userId')));
    }
  }, []);


  const updateChanges = (value) => {
    onChangeEditorValues(value);
  };


  const onPasteContnet = useCallback((event) => {
    const html = event.clipboardData?.getData("text/html");
    const text = event.clipboardData?.getData("text/plain");

    if (html) {
      event.preventDefault();

      let parsed = deserializeHTMLToSlate(html); // Your custom deserializer

      if (parsed && parsed.length > 0) {
        const safeNodes = fixSlateDocument(parsed); // ⬅️ Prevents structural errors
        Transforms.insertFragment(editor, safeNodes);
      } else if (text) {
        Transforms.insertText(editor, text);
      }

    } else if (text) {
      event.preventDefault();
      Transforms.insertText(editor, text);
    }
  }, [editor]);


  const calculatePagination = () => {
    const pages = editorContainerRef.current?.querySelectorAll(".page");
    setTotalPages(pages?.length || 1);
  };

  useEffect(() => {
    const handleScroll = () => {
      const pages = editorContainerRef.current?.querySelectorAll(".page");
      const scrollTop = editorContainerRef.current?.scrollTop || 0;
      if (pages?.length) {
        const pageIndex = Math.floor(scrollTop / PAGE_HEIGHT) + 1;
        setCurrentPage(pageIndex);
      }
    };
    const container = editorContainerRef.current;
    container?.addEventListener("scroll", handleScroll);
    calculatePagination();
    return () => container?.removeEventListener("scroll", handleScroll);
  }, []);

  console.log("initialValues",initialValues, currentPage, totalPages);

  return (
    <div className={styles.wrapper}>
      {loading && <KoLoader />}
      <Slate editor={editor} initialValue={initialValues} onChange={(val) => updateChanges(val)}>
        <Header className={styles.shallowHeader}></Header>
        <Header className={styles.globalHeader}>
          <div className={styles.docHeader}>
            <div className={styles.docHeaderLeft}>
              <FileTypeIcon type={'docx'} size={32} />
              <KoInput disabled={!canEdit} onChange={(e) => setRenameDocument(e.target.value)} onBlur={() => onUpdateName(fileName)} className={`${styles.titleInput} ${canEdit ? '' : styles.titleInputDisabled}`} value={fileName} />
            </div>
            <div className={styles.docHeaderRight}>
              {isLoading && <div className={styles.loader}>
                <KoIcon color="#007867" name="RefreshOutlined" width={16} height={16} />
                <KoText className={styles.loaderText} text={savingTextStatus || 'asdasdasd'} />
              </div>}
              <KoTooltips title={<div className={styles.lastUpdated}>
                <KoText className={styles.lastUpdatedText} text={'Last Updated:'} />
                <KoDateFormatter className={styles.lastUpdatedDate} data={fileDetails?.updatedAt !== '0001-01-01T00:00:00Z' ? fileDetails?.updatedAt : fileDetails?.createdAt} />
              </div>} placement="bottom">
                <KoButton iconBtn={true} color="default" variant="filled" icon={<KoIcon width={16} height={16} name={'LogOutlined'} />} />
              </KoTooltips>
              <KoTooltips title={'Comments'} placement="bottom">
                <KoBadge count={totalComments} >
                  <KoButton disabled={!canEdit && !canComment} onClick={onComments} iconBtn={true} color="default" variant="filled" icon={<KoIcon width={20} height={20} name={'ChatOutlined'} />} />
                </KoBadge>
              </KoTooltips>
              <KoTooltips
                title={`Shared with ${fileDetails?.sharedWith?.users?.length || 0
                  } ${fileDetails?.sharedWith?.users?.length === 1 ? 'person' : 'people'} & ${fileDetails?.sharedWith?.groups?.length || 0
                  } ${fileDetails?.sharedWith?.groups?.length === 1 ? 'group' : 'groups'}`}
                placement="bottom"
              >
                <KoButton disabled={!canEdit} onClick={onShare} className={styles.shareButton} color="default" variant="filled" label='Share' icon={<KoIcon width={20} height={20} name={'AddtoGroupsOutlined'} color={'#FFF'} />} />
              </KoTooltips>
              <KoTooltips title={'Download'} placement="bottom">
                <KoButton loader={isLoading} onClick={onDownload} iconBtn={true} color="default" variant="filled" icon={<KoIcon width={20} height={20} name={'DownloadOutlined'} />} />
              </KoTooltips>

              <div className={styles.userWrapper} >
                {accountDetails?.companyName && <KoText ellipsis={true} className={styles.name} text={`${accountDetails?.companyName}.`} />}
                <div className={`${styles.headCircle}  ${styles[userDetails ?
                  userDetails?.profile?.firstName?.charAt(0).toLowerCase() : 'a']}`}>
                  {userDetails?.profile?.['logo'] ?
                    <KoDP shape={'circle'} logo={userDetails?.profile?.logo} width={userDetails?.profile?.logo?.[0]?.name ? 30 : 30} height={userDetails?.profile?.logo?.[0]?.name ? 30 : 30} />
                    :
                    <KoText className={`${styles.letter}`} text={userDetails?.profile?.firstName?.charAt(0)} />
                  }
                </div>
              </div>
            </div>
          </div>
          <Toolbar  disabled={!canEdit} className={styles.toolbar} folderId={folderId} fontSize={fontSize} editor={editor} setFontSize={setFontSize} onUpdateAttachment={onUpdateAttachment} />
        </Header>
        <div className={styles.pageContainer} ref={editorContainerRef}>
          <div className={`${styles.card} richTextWrapper`}>
            <HoveringToolbar editor={editor} />
            <Editable
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              placeholder="Start typing here..."
              spellCheck
              autoFocus
              readOnly={!canEdit}
              onPaste={async(event) => {
                const items = event.clipboardData?.items || [];
                for (const item of items) {
                  if (item.type.indexOf("image") !== -1) {
                    event.preventDefault();
                    const file = item.getAsFile();
                    if (file) {
                      await uploadImageAndInsert(editor, file, onUpdateAttachment, lookupId, workspaceId, commentId, setLoading, folderId);
                    }
                    return;
                  }
                }
                onPasteContnet(event);
              }}
              onDrop={async(event) => {
                event.preventDefault();
                const data = event.dataTransfer.getData("application/x-slate-fragment");
                const files = Array.from(event.dataTransfer.files);
                for (const file of files) {
                  if (file.type.startsWith("video/")) {
                    await uploadVideoFile(editor, file, onUpdateAttachment, lookupId, workspaceId, commentId, setLoading, folderId);
                  }
                  if (file.type.startsWith("image/")) {
                    event.preventDefault();
                    await uploadImageAndInsert(editor, file, onUpdateAttachment, lookupId, workspaceId, commentId, setLoading, folderId);
                  }
                }
                if (data) {
                  const node = JSON.parse(data);
                  const point = ReactEditor.findEventRange(editor, event).focus;
                  Transforms.select(editor, point);
                  Transforms.insertNodes(editor, node);
                }
              }}
              onKeyDown={(event) => {
                if (!(event.ctrlKey || event.metaKey)) return;
                switch (event.key.toLowerCase()) {
                case "b":
                  event.preventDefault();
                  toggleMark(editor, "bold");
                  break;
                case "i":
                  event.preventDefault();
                  toggleMark(editor, "italic");
                  break;
                case "u":
                  event.preventDefault();
                  toggleMark(editor, "underline");
                  break;
                case "`":
                  event.preventDefault();
                  toggleMark(editor, "code");
                  break;
                default:
                  break;
                }
              }}
            />
          </div>
        </div>
      </Slate>
    </div>
  );
};

export default KoFileRichTextEditor;
